import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db, COLLECTIONS } from '@/services/firebase';

interface AuthState {
  user: User | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  // Actions
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  checkAdminStatus: (user: User) => Promise<{ isAdmin: boolean; isSuperAdmin: boolean }>;
  initialize: () => () => void;
}

// Admin UIDs are stored in Firestore config/settings document
// No hardcoded UIDs - all admin access is managed via Firestore

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAdmin: false,
      isSuperAdmin: false,
      isLoading: true,
      error: null,

      // Sign in with Google OAuth
      signInWithGoogle: async () => {
        try {
          set({ isLoading: true, error: null });

          const result = await signInWithPopup(auth, googleProvider);
          const user = result.user;

          // Check if user is admin/super admin
          const { isAdmin, isSuperAdmin } = await get().checkAdminStatus(user);

          if (!isAdmin) {
            // Sign out non-admin users trying to access admin panel
            await firebaseSignOut(auth);
            set({
              user: null,
              isAdmin: false,
              isSuperAdmin: false,
              isLoading: false,
              error: 'Access denied. Only authorized admins can access this panel.',
            });
            return;
          }

          set({
            user,
            isAdmin: true,
            isSuperAdmin,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Sign in error:', error);
          set({
            user: null,
            isAdmin: false,
            isSuperAdmin: false,
            isLoading: false,
            error: error instanceof Error ? error.message : 'Sign in failed',
          });
        }
      },

      // Sign out
      signOut: async () => {
        try {
          set({ isLoading: true });
          await firebaseSignOut(auth);
          set({
            user: null,
            isAdmin: false,
            isSuperAdmin: false,
            isLoading: false,
            error: null,
          });
        } catch (error) {
          console.error('Sign out error:', error);
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Sign out failed',
          });
        }
      },

      // Check if user has admin privileges
      checkAdminStatus: async (user: User): Promise<{ isAdmin: boolean; isSuperAdmin: boolean }> => {
        try {
          // Method 1: Check Firestore config/settings for admin UIDs (primary method)
          try {
            const adminDoc = await getDoc(
              doc(db, COLLECTIONS.CONFIG, 'settings')
            );
            if (adminDoc.exists()) {
              const settings = adminDoc.data();
              const superAdminUids: string[] = settings?.superAdminUids || [];
              const adminUids: string[] = settings?.adminUids || [];

              if (superAdminUids.includes(user.uid)) {
                return { isAdmin: true, isSuperAdmin: true };
              }
              if (adminUids.includes(user.uid)) {
                return { isAdmin: true, isSuperAdmin: false };
              }
            }
          } catch (firestoreError) {
            console.warn('Could not check admin UIDs from Firestore:', firestoreError);
          }

          // Method 2: Check Firebase custom claims as fallback
          try {
            const idTokenResult = await user.getIdTokenResult();
            if (idTokenResult.claims.superAdmin === true) {
              return { isAdmin: true, isSuperAdmin: true };
            }
            if (idTokenResult.claims.admin === true) {
              return { isAdmin: true, isSuperAdmin: false };
            }
          } catch (claimsError) {
            console.warn('Could not check custom claims:', claimsError);
          }

          // Not an admin
          return { isAdmin: false, isSuperAdmin: false };
        } catch (error) {
          console.error('Error checking admin status:', error);
          return { isAdmin: false, isSuperAdmin: false };
        }
      },

      // Initialize auth state listener
      initialize: () => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (user) {
            const { isAdmin, isSuperAdmin } = await get().checkAdminStatus(user);
            set({
              user,
              isAdmin,
              isSuperAdmin,
              isLoading: false,
            });
          } else {
            set({
              user: null,
              isAdmin: false,
              isSuperAdmin: false,
              isLoading: false,
            });
          }
        });

        return unsubscribe;
      },
    }),
    {
      name: 'nepro-auth-storage',
      partialize: (state) => ({
        // Only persist non-sensitive data
        isAdmin: state.isAdmin,
        isSuperAdmin: state.isSuperAdmin,
      }),
    }
  )
);
